import type { RaceState } from "./race-engine";
import type { GateRef, RaceDataset, TrajectoryPoint } from "./race-types";

const CONFIG = {
  GATE_RENDER_RADIUS_M: 10,
  PATH_COLOR: "rgba(255,107,26,0.9)",
  DRONE_COLOR: "#38BDF8",
  GATE_COLOR_PENDING: "#3D4A5E",
  GATE_COLOR_PASSED: "#FF6B1A",
  GATE_COLOR_ACTIVE: "#FF8534",
  Y_COMPRESS: 0.72,
  Z_LIFT: 0.45,
  Z_REF_OFFSET_M: 0,
  TRAIL_FULL_MS: 6000,
  TRAIL_FADE_MS: 30000,
  TRAIL_MIN_ALPHA: 0.04,
  TRAIL_WIDTH_MAX: 2.6,
  TRAIL_WIDTH_MIN: 0.8,
  PULSE_LIFE_MS: 2800,
  PULSE_MAX_SCALE: 3.0,
} as const;

export interface ProjState {
  scale: number;
  ox: number;
  oy: number;
  viewW: number;
  viewH: number;
  zRef: number;
}

export interface ViewState {
  zoom: number;
  panX: number;
  panY: number;
}

export function createViewState(): ViewState {
  return { zoom: 1, panX: 0, panY: 0 };
}

function effVy(y: number, z: number, zRef: number) {
  return y * CONFIG.Y_COMPRESS + (z - zRef) * CONFIG.Z_LIFT;
}

export function fitProjection(dataset: RaceDataset, rect: DOMRect): ProjState {
  const pad = 56;
  const bb = dataset.bbox;
  const zRef = dataset.home.alt + CONFIG.Z_REF_OFFSET_M;

  const effYs = dataset.gates.map((g) => effVy(g.y, g.alt, zRef));
  effYs.push(effVy(0, zRef, zRef));
  effYs.push(effVy(bb.min_y, zRef, zRef));
  effYs.push(effVy(bb.max_y, zRef, zRef));
  const effMinY = Math.min(...effYs);
  const effMaxY = Math.max(...effYs);

  const worldW = bb.max_x - bb.min_x;
  const worldH = effMaxY - effMinY;
  const scaleX = (rect.width - 2 * pad) / Math.max(worldW, 1);
  const scaleY = (rect.height - 2 * pad) / Math.max(worldH, 1);
  const scale = Math.min(scaleX, scaleY);
  const ox = pad + ((rect.width - 2 * pad) - worldW * scale) / 2 - bb.min_x * scale;
  const oy = pad + ((rect.height - 2 * pad) - worldH * scale) / 2 + effMaxY * scale;

  return { scale, ox, oy, viewW: rect.width, viewH: rect.height, zRef };
}

function toPx(
  x: number,
  y: number,
  z: number | null,
  proj: ProjState,
  view: ViewState,
) {
  const useZ = z === null || z === undefined ? proj.zRef : z;
  const vy = effVy(y, useZ, proj.zRef);
  const px0 = x * proj.scale + proj.ox;
  const py0 = -vy * proj.scale + proj.oy;
  const cx = proj.viewW / 2;
  const cy = proj.viewH / 2;
  return {
    px: (px0 - cx) * view.zoom + cx + view.panX,
    py: (py0 - cy) * view.zoom + cy + view.panY,
  };
}

function findTrajIdx(arr: TrajectoryPoint[], t: number) {
  if (!arr.length) return 0;
  if (t <= arr[0].t) return 0;
  let lo = 0;
  let hi = arr.length - 1;
  if (t >= arr[hi].t) return hi;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (arr[mid].t <= t) lo = mid;
    else hi = mid;
  }
  return lo;
}

export function interpPos(arr: TrajectoryPoint[], t: number): TrajectoryPoint | null {
  if (!arr.length) return null;
  if (t <= arr[0].t) return { ...arr[0] };
  if (t >= arr[arr.length - 1].t) return { ...arr[arr.length - 1] };
  const i = findTrajIdx(arr, t);
  const a = arr[i];
  const b = arr[i + 1];
  const alpha = (t - a.t) / Math.max(b.t - a.t, 1);
  return {
    t,
    lat: a.lat,
    lon: a.lon,
    x: a.x + (b.x - a.x) * alpha,
    y: a.y + (b.y - a.y) * alpha,
    alt: a.alt + (b.alt - a.alt) * alpha,
    yaw: a.yaw + (b.yaw - a.yaw) * alpha,
    pitch: a.pitch,
    roll: a.roll,
    vx: a.vx,
    vy: a.vy,
    vz: a.vz,
    speed_ms: a.speed_ms + (b.speed_ms - a.speed_ms) * alpha,
  };
}

function strokePath(ctx: CanvasRenderingContext2D, pts: { px: number; py: number }[]) {
  ctx.beginPath();
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    if (i === 0) ctx.moveTo(p.px, p.py);
    else ctx.lineTo(p.px, p.py);
  }
  ctx.stroke();
}

function gateRingPath(
  g: GateRef,
  radiusM: number,
  proj: ProjState,
  view: ViewState,
) {
  const y = (g.yaw * Math.PI) / 180;
  const ux = Math.cos(y);
  const uy = -Math.sin(y);
  const samples = 32;
  const pts: { px: number; py: number }[] = [];
  for (let i = 0; i <= samples; i++) {
    const phi = (i / samples) * Math.PI * 2;
    const c = Math.cos(phi);
    const s = Math.sin(phi);
    const wx = g.x + radiusM * c * ux;
    const wy = g.y + radiusM * c * uy;
    const wz = g.alt + radiusM * s;
    pts.push(toPx(wx, wy, wz, proj, view));
  }
  return pts;
}

function drawGrid(ctx: CanvasRenderingContext2D, proj: ProjState, view: ViewState) {
  const step = 50;
  const stepPx = step * proj.scale * view.zoom;
  if (stepPx < 12) return;
  const { viewW, viewH, ox, oy } = proj;
  ctx.strokeStyle = "rgba(42,52,68,0.35)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  const startX = (ox + view.panX) % stepPx;
  const startY = (oy + view.panY) % stepPx;
  for (let x = startX; x < viewW; x += stepPx) {
    ctx.moveTo(x, 0);
    ctx.lineTo(x, viewH);
  }
  for (let y = startY; y < viewH; y += stepPx) {
    ctx.moveTo(0, y);
    ctx.lineTo(viewW, y);
  }
  ctx.stroke();
}

function drawHome(ctx: CanvasRenderingContext2D, proj: ProjState, view: ViewState) {
  const { px, py } = toPx(0, 0, proj.zRef, proj, view);
  ctx.save();
  ctx.translate(px, py);
  ctx.strokeStyle = "#A0AAB8";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(0, 0, 6, 0, Math.PI * 2);
  ctx.moveTo(-10, 0);
  ctx.lineTo(10, 0);
  ctx.moveTo(0, -10);
  ctx.lineTo(0, 10);
  ctx.stroke();
  ctx.fillStyle = "rgba(160,170,184,0.85)";
  ctx.font = '500 9px "JetBrains Mono", monospace';
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("HOME · SPAWN", 12, -14);
  ctx.restore();
}

function drawGateGround(
  ctx: CanvasRenderingContext2D,
  g: GateRef,
  proj: ProjState,
  view: ViewState,
) {
  const y = (g.yaw * Math.PI) / 180;
  const ux = Math.cos(y);
  const uy = -Math.sin(y);
  const R = CONFIG.GATE_RENDER_RADIUS_M;
  const a = toPx(g.x - R * ux, g.y - R * uy, proj.zRef, proj, view);
  const b = toPx(g.x + R * ux, g.y + R * uy, proj.zRef, proj, view);
  const groundC = toPx(g.x, g.y, proj.zRef, proj, view);
  const topC = toPx(g.x, g.y, g.alt, proj, view);

  ctx.lineCap = "round";
  ctx.strokeStyle = "rgba(61,74,94,0.65)";
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(a.px, a.py);
  ctx.lineTo(b.px, b.py);
  ctx.stroke();

  ctx.setLineDash([2, 3]);
  ctx.strokeStyle = "rgba(107,115,132,0.45)";
  ctx.beginPath();
  ctx.moveTo(groundC.px, groundC.py);
  ctx.lineTo(topC.px, topC.py);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.fillStyle = "rgba(61,74,94,0.8)";
  ctx.beginPath();
  ctx.arc(groundC.px, groundC.py, 2.5, 0, Math.PI * 2);
  ctx.fill();
}

function drawGate(
  ctx: CanvasRenderingContext2D,
  g: GateRef,
  raceState: RaceState,
  proj: ProjState,
  view: ViewState,
) {
  const { nGates } = raceState;
  const isNext = raceState.lastPass === null
    ? g.idx === 1
    : g.idx === (((raceState.lastPass.gate_idx - 1) % nGates) + 1 + 1 - 1 + 1);
  const nextExpected = raceState.lastPass
    ? (raceState.lastPass.gate_idx % nGates) + 1
    : 1;
  const actualIsNext = g.idx === nextExpected;
  const passed = raceState.passedGates.has(g.idx);
  const R = CONFIG.GATE_RENDER_RADIUS_M;
  const color = actualIsNext
    ? CONFIG.GATE_COLOR_ACTIVE
    : passed
      ? CONFIG.GATE_COLOR_PASSED
      : CONFIG.GATE_COLOR_PENDING;

  void isNext;

  const ringPts = gateRingPath(g, R, proj, view);
  const half = Math.floor(ringPts.length / 2);
  const back = ringPts.slice(0, half + 1);
  const front = ringPts.slice(half);

  ctx.lineWidth = 1.5;
  ctx.strokeStyle = actualIsNext
    ? "rgba(255,133,52,0.45)"
    : passed
      ? "rgba(255,107,26,0.35)"
      : "rgba(61,74,94,0.55)";
  strokePath(ctx, back);

  const innerPts = gateRingPath(g, R * 0.7, proj, view);
  ctx.setLineDash([3, 3]);
  ctx.lineWidth = 1;
  ctx.strokeStyle = actualIsNext
    ? "rgba(255,133,52,0.35)"
    : passed
      ? "rgba(255,107,26,0.22)"
      : "rgba(61,74,94,0.4)";
  strokePath(ctx, innerPts);
  ctx.setLineDash([]);

  ctx.lineWidth = actualIsNext ? 2.8 : 2.2;
  ctx.strokeStyle = color;
  if (actualIsNext) {
    ctx.shadowColor = "rgba(255,133,52,0.6)";
    ctx.shadowBlur = 12;
  } else if (passed) {
    ctx.shadowColor = "rgba(255,107,26,0.35)";
    ctx.shadowBlur = 6;
  }
  strokePath(ctx, front);
  ctx.shadowBlur = 0;

  const topPt = toPx(g.x, g.y, g.alt + R + 4, proj, view);
  const bgR = 11;
  ctx.beginPath();
  ctx.arc(topPt.px, topPt.py, bgR, 0, Math.PI * 2);
  ctx.fillStyle = actualIsNext
    ? CONFIG.GATE_COLOR_ACTIVE
    : passed
      ? "rgba(26,34,48,0.95)"
      : "rgba(10,14,20,0.95)";
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.fillStyle = actualIsNext ? "#0A0E14" : "#E8ECF1";
  ctx.font = '600 11px "JetBrains Mono", monospace';
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(g.idx), topPt.px, topPt.py);

  const n = raceState.passCount.get(g.idx) || 0;
  if (n > 0) {
    ctx.fillStyle = "rgba(255,107,26,0.95)";
    ctx.font = '600 10px "JetBrains Mono", monospace';
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(`×${n}`, topPt.px + bgR + 4, topPt.py);
  }
}

function drawTrajectory(
  ctx: CanvasRenderingContext2D,
  dataset: RaceDataset,
  currentT: number,
  proj: ProjState,
  view: ViewState,
) {
  const arr = dataset.trajectory;
  if (arr.length < 2) return;
  const upto = findTrajIdx(arr, currentT);
  const full = CONFIG.TRAIL_FULL_MS;
  const fade = CONFIG.TRAIL_FADE_MS;
  const minA = CONFIG.TRAIL_MIN_ALPHA;
  const wMax = CONFIG.TRAIL_WIDTH_MAX;
  const wMin = CONFIG.TRAIL_WIDTH_MIN;

  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (let i = 1; i <= upto; i++) {
    const a = arr[i - 1];
    const b = arr[i];
    const segAge = currentT - b.t;
    let alpha: number;
    if (segAge <= full) alpha = 1.0;
    else if (segAge <= full + fade) {
      const k = (segAge - full) / fade;
      alpha = 1.0 - k * (1.0 - minA);
    } else alpha = minA;
    const width = wMin + (wMax - wMin) * Math.max(minA, alpha);
    const pA = toPx(a.x, a.y, a.alt, proj, view);
    const pB = toPx(b.x, b.y, b.alt, proj, view);
    ctx.strokeStyle = `rgba(255,107,26,${alpha.toFixed(3)})`;
    ctx.lineWidth = width;
    ctx.shadowColor = alpha >= 0.35 ? "rgba(255,107,26,0.35)" : "transparent";
    ctx.shadowBlur = alpha >= 0.35 ? 4 : 0;
    ctx.beginPath();
    ctx.moveTo(pA.px, pA.py);
    ctx.lineTo(pB.px, pB.py);
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
}

function drawDrone(
  ctx: CanvasRenderingContext2D,
  dataset: RaceDataset,
  currentT: number,
  proj: ProjState,
  view: ViewState,
) {
  const cur = interpPos(dataset.trajectory, currentT);
  if (!cur) return;
  const top = toPx(cur.x, cur.y, cur.alt, proj, view);
  const ground = toPx(cur.x, cur.y, proj.zRef, proj, view);
  const yawRad = (cur.yaw * Math.PI) / 180;

  ctx.setLineDash([2, 3]);
  ctx.strokeStyle = "rgba(56,189,248,0.35)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(ground.px, ground.py);
  ctx.lineTo(top.px, top.py);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.beginPath();
  ctx.ellipse(ground.px, ground.py, 8, 8 * CONFIG.Y_COMPRESS, 0, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fill();
  ctx.strokeStyle = "rgba(56,189,248,0.35)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.save();
  ctx.translate(top.px, top.py);
  ctx.rotate(yawRad);

  ctx.strokeStyle = CONFIG.DRONE_COLOR;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(0, 0, 9, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = CONFIG.DRONE_COLOR;
  ctx.shadowColor = "rgba(56,189,248,0.8)";
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.moveTo(0, -10);
  ctx.lineTo(5, 4);
  ctx.lineTo(-5, 4);
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;

  ctx.restore();
}

export function renderRace(
  ctx: CanvasRenderingContext2D,
  dataset: RaceDataset,
  raceState: RaceState,
  currentT: number,
  proj: ProjState,
  view: ViewState,
) {
  ctx.clearRect(0, 0, proj.viewW, proj.viewH);
  drawGrid(ctx, proj, view);
  drawHome(ctx, proj, view);
  for (const g of dataset.gates) drawGateGround(ctx, g, proj, view);
  drawTrajectory(ctx, dataset, currentT, proj, view);
  for (const g of dataset.gates) drawGate(ctx, g, raceState, proj, view);
  drawDrone(ctx, dataset, currentT, proj, view);
}
